import axios from 'axios'
import type { Trade } from '@/lib/types'

const isProduction = typeof window !== 'undefined' && window.location.hostname !== 'localhost'
const API_BASE_URL = isProduction ? 'https://api.futurabroker.com/api/v2' : 'http://localhost:4000/api/v2'

console.log('[SIGMA] API_BASE_URL:', API_BASE_URL, isProduction ? '(production)' : '(development)')

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Interceptor para adicionar token de autenticação
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('sigma_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Interceptor para tratamento de erros
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado ou inválido
      localStorage.removeItem('sigma_token')
      localStorage.removeItem('sigma_user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  },
)

export const apiService = {
  // Buscar histórico de operações
  async fetchTrades(): Promise<Trade[]> {
    try {
      const response = await apiClient.get('/history/transactions', {
        params: { include_stats: 'false' },
      })
      const transactions = response.data.data?.transactions || []

      console.log('[SIGMA] Total de transações:', transactions.length)

      // Retornar dados diretos da API com notas do localStorage
      return transactions.map((t: any) => ({
        ...t,
        notes: this.getTradeNotes(t.id),
      }))
    } catch (error) {
      console.error('Erro ao buscar trades:', error)
      return []
    }
  },

  // Buscar saldo atual do usuário
  async fetchBalance(): Promise<number> {
    try {
      const response = await apiClient.get('/wallet/balance')
      console.log('[SIGMA] Balance response:', response.data)
      return response.data.data?.balance || 0
    } catch (error) {
      console.error('Erro ao buscar saldo:', error)
      return 0
    }
  },

  // Buscar informações do usuário
  async fetchUser() {
    try {
      const response = await apiClient.get('/auth/me')
      return response.data
    } catch (error) {
      console.error('Erro ao buscar usuário:', error)
      return null
    }
  },

  // Atualizar notas de uma operação (local storage por enquanto)
  async updateTradeNotes(tradeId: number, notes: string): Promise<boolean> {
    try {
      const storedNotes = localStorage.getItem('trade_notes') || '{}'
      const notesObj = JSON.parse(storedNotes)
      notesObj[tradeId] = notes
      localStorage.setItem('trade_notes', JSON.stringify(notesObj))
      return true
    } catch (error) {
      console.error('Erro ao atualizar notas:', error)
      return false
    }
  },

  // Buscar notas de operações (local storage)
  getTradeNotes(tradeId: number): string {
    try {
      const storedNotes = localStorage.getItem('trade_notes') || '{}'
      const notesObj = JSON.parse(storedNotes)
      return notesObj[tradeId] || ''
    } catch (error) {
      return ''
    }
  },

  // Buscar estatísticas da wallet (depósitos e saques)
  async fetchWalletStats(): Promise<{
    deposits: { pending: number; paid: number; failed: number; total: number }
    withdrawals: { pending: number; paid: number; failed: number; retained: number; total: number }
  }> {
    try {
      const response = await apiClient.get('/wallet/stats')
      console.log('[SIGMA] Wallet stats response:', response.data)
      return response.data.data || {
        deposits: { pending: 0, paid: 0, failed: 0, total: 0 },
        withdrawals: { pending: 0, paid: 0, failed: 0, retained: 0, total: 0 },
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas da wallet:', error)
      return {
        deposits: { pending: 0, paid: 0, failed: 0, total: 0 },
        withdrawals: { pending: 0, paid: 0, failed: 0, retained: 0, total: 0 },
      }
    }
  },
}

export default apiClient
